import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, FolderClosed, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useProjects, useSaveResearch } from "@/hooks/queries";

export function SaveToProjectDialog({ session, open, onOpenChange }) {
  const { data } = useProjects("active");
  const save = useSaveResearch();
  const [target, setTarget] = useState(undefined);
  const pick = async (projectId) => {
    setTarget(projectId);
    try {
      const res = await save.mutateAsync({ id: session.id, projectId });
      const project = data?.items?.find((p) => p.id === projectId);
      toast.success(project ? `Saved to ${project.name}` : "Saved to Files", {
        action: { label: "Open", onClick: () => window.location.assign(project ? `/projects/${project.id}` : "/files") },
      });
      onOpenChange(false);
      return res;
    } catch (e) {
      toast.error(e.requestId ? `${e.message} (Ref ${e.requestId})` : e.message);
      return null;
    }
  };
  const Row = ({ id, label, icon: Icon, hint }) => (
    <button type="button" disabled={save.isPending} onClick={() => pick(id)} data-testid={`research-save-target-${id || "files"}`} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/[0.05] disabled:opacity-60">
      <Icon className="h-4 w-4 shrink-0 text-zinc-400" strokeWidth={1.6} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-zinc-100">{label}</span>
        {hint && <span className="block text-[11.5px] text-zinc-500">{hint}</span>}
      </span>
      {session.project_id === id && id && <Check className="h-4 w-4 text-zinc-400" />}
      {save.isPending && target === id && <span className="text-xs text-zinc-500">Saving…</span>}
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-ink-850 sm:max-w-md" data-testid="research-save-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Save report</DialogTitle>
          <DialogDescription className="text-zinc-500">Saved as a Markdown file with its sources. In a project, ChatSleek uses it as context in that project's chats.</DialogDescription>
        </DialogHeader>
        <div className="mt-1 max-h-[340px] space-y-0.5 overflow-y-auto">
          {(data?.items || []).map((p) => <Row key={p.id} id={p.id} label={p.name} icon={FolderClosed} />)}
          {data && !data.items.length && (
            <p className="px-3 py-4 text-center text-sm text-zinc-500">
              No projects yet. <Link to="/projects" className="underline underline-offset-4">Create one</Link> or save to Files.
            </p>
          )}
          <Row id={null} label="Files only" hint="Keep it in your file library without a project" icon={FileText} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
